import { Book, ChevronDown, ChevronRight, Eye, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";
import { CourseDetailSection } from "@/routes/course/types/courseDetail.ts";
import { TimeHelper } from "@/services/helpers/timeHelpers.ts";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type CourseSections = {
  sections: CourseDetailSection[];
  isOwned: boolean;
};
const CourseContent = ({ sections, isOwned }: CourseSections) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId],
    );
  };

  const sortedSections = [...sections].sort(
    (a, b) => a.orderNumber - b.orderNumber,
  );

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">{t("course.content")}</h2>

      {sortedSections.map((section, sectionIndex) => {
        const isExpanded = expandedSections.includes(section.id);

        return (
          <Card key={section.id} className="overflow-hidden">
            <CardHeader
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleSection(section.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                  <CardTitle>
                    {sectionIndex + 1}. {section.name}
                  </CardTitle>
                </div>
                <span className="text-sm text-muted-foreground">
                  {TimeHelper.convertMinuteToText(section.durationMinute)}
                </span>
              </div>
            </CardHeader>
            {isExpanded && (
              <CardContent className="border-t">
                <div className="space-y-2 pt-4">
                  {section.lectures
                    .sort((a, b) => a.orderNumber - b.orderNumber)
                    .map((lecture) => {
                      const lectureNumber = lecture.orderNumber + 1;
                      const duration = TimeHelper.convertMinuteToText(
                        lecture.durationMinute,
                      );

                      if (isOwned || lecture.isPublic) {
                        return (
                          <Link
                            key={lecture.id}
                            to={`/course/${section.courseId}/${lecture.id}`}
                            className="flex items-center py-2 hover:bg-muted/50 rounded-md px-2 transition-colors"
                          >
                            <span className="text-sm font-medium text-muted-foreground mr-3 flex-shrink-0 w-6">
                              {lectureNumber}.
                            </span>
                            {isOwned ? (
                              <Book className="h-5 w-5 mr-3 text-muted-foreground flex-shrink-0" />
                            ) : (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Eye className="h-5 w-5 mr-3 text-orange-500 flex-shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t("course.freeAccess")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <div className="flex-1">
                              <span className="block">{lecture.name}</span>
                            </div>
                            <div className="ml-4">{duration}</div>
                          </Link>
                        );
                      }

                      return (
                        <div
                          key={lecture.id}
                          className="flex items-center py-2 px-2 opacity-50 cursor-not-allowed rounded-md"
                        >
                          <span className="text-sm font-medium text-muted-foreground mr-3 flex-shrink-0 w-6">
                            {lectureNumber}.
                          </span>
                          <Lock className="h-5 w-5 mr-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1">
                            <span className="block text-muted-foreground">
                              {lecture.name}
                            </span>
                          </div>
                          <div className="ml-4 text-muted-foreground">
                            {duration}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};
export default CourseContent;
